import React, { useContext, useRef, useState } from 'react';
import {
  arrayOf, bool, func, number, shape, string
} from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { InstantCheckout } from '@thd-olt-component-react/instant-checkout';
import { Sticky } from '@thd-olt-component-react/sticky';
import { Quantity } from '@thd-olt-component-react/quantity';
import classNames from 'classnames/bind';
import {
  DetaCountDownTimer,
  getMaxDeliverableQty
} from '@thd-olt-component-react/fulfillment';
import { BuyboxAddToCart } from './BuyboxAddToCart';
import { BuyboxSaveToFavorites } from './BuyboxSaveToFavorites';
import { BuyboxAddToQuote } from './BuyboxAddToQuote';
import { Paypal } from './Paypal';
import {
  getCartOptions,
  getCartReqParams,
  shouldDisplaySubbuybox,
  getFulfillmentLocation
} from '../../BuyboxHelper';

import { useBogoInfo } from '../hooks/useBogoInfo';

import '../buybox.scss';
import { formatDate, isMobile } from '../../util';
import { FULFILLMENT_METHOD_STH } from '../../constants';

const BuyboxActions = ({
  alterBrowserHistory,
  disableATC,
  fulfillment,
  fulfillmentMethod,
  hideInstantCheckout,
  hideQuantity,
  isAssembleItemAtStore,
  itemId,
  configurableItems,
  minimumOrderQuantity,
  onATCClick,
  onQuantityChange,
  paypalEligible,
  productAddOns = {},
  quantity,
  quantityLimitExceed,
  hidePaypalCheckout,
  sticky,
  storeId,
  storeZip,
  zipCode,
  showCards,
  showFavoriteAndQuote,
  isTwoTileEnabledFS,
  maxQuantityExceeded = false,
  quantityLimit
}) => {
  const { channel, isConsumerApp } = useContext(ExperienceContext);

  const buyboxActionsRef = useRef(null);
  const bogoInfo = useBogoInfo();
  const isBogoSelected = bogoInfo?.bogoSelectedProduct?.itemId;

  const buyboxActionClasses = classNames('buybox__actions', {
    'buybox__actions--margin-bottom': isBogoSelected
  });

  const quantityNode = !hideQuantity && (
    <div className="buybox__quantity--wrapper">
      <Quantity
        className={quantityLimitExceed ? 'buybox__quantity--warning' : ''}
        minimumQuantity={minimumOrderQuantity || 1}
        onChange={onQuantityChange}
        value={quantity}
        showWarningState={maxQuantityExceeded}
        quantityLimit={isTwoTileEnabledFS ? quantityLimit : 0}
      />
    </div>
  );

  const addToCartNode = (
    <BuyboxAddToCart
      alterBrowserHistory={alterBrowserHistory}
      disableATC={disableATC}
      fulfillment={fulfillment}
      fulfillmentMethod={fulfillmentMethod}
      isAssembleItemAtStore={isAssembleItemAtStore}
      itemId={itemId}
      configurableItems={configurableItems}
      onATCClick={onATCClick}
      productAddOns={productAddOns}
      quantity={quantity}
      quantityLimitExceed={quantityLimitExceed}
      storeId={storeId}
      storeZip={storeZip}
      zipCode={zipCode}
      showFavoriteAndQuote={showFavoriteAndQuote}
    />
  );

  const deliveryService = (fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'delivery');
  const sthFulfillment = (deliveryService?.services || []).find((service) => service.type === 'sth');
  const sthLocation = (sthFulfillment?.locations || []).find((location) => location?.type === 'online');
  const sthInventory = sthLocation?.inventory?.quantity || 0;

  const bodfsFulfillment = (deliveryService?.services || []).find((service) => service.type === 'express delivery');
  const bodfsLocation = (bodfsFulfillment?.locations || []).find((location) => location?.type === 'store');

  const [showCountdownTime, changeCountdownTimeStatus] = useState(true);
  const removeTimer = () => changeCountdownTimeStatus(false);
  const showDetaTimer = isTwoTileEnabledFS && fulfillmentMethod === FULFILLMENT_METHOD_STH;

  const showInstantCheckout = () => {

    const { attachedLabor = [], service = {}, subscriptions = {}, dualPath } = productAddOns || {};

    if (fulfillmentMethod !== FULFILLMENT_METHOD_STH) {
      return false;
    }

    if (quantity > sthInventory) {
      return false;
    }

    if ((attachedLabor.length && attachedLabor[0].attachedLaborSku)
      || (service?.type && service?.category === 'assembly')
      || subscriptions.frequency
      || dualPath) {
      return false;
    }

    if (disableATC || quantityLimitExceed || hideInstantCheckout) {
      return false;
    }

    return true;
  };

  const stickyClassnames = classNames('buybox__actions', {
    'buybox__actions--sticky': !isConsumerApp && !showFavoriteAndQuote,
    'buybox__actions--sticky-app': isConsumerApp && !showFavoriteAndQuote,
    'buybox__actions--sticky-favorites': !isConsumerApp && showFavoriteAndQuote
  });

  return (
    <>
      {sticky && isMobile(channel) && (
        <Sticky
          boxShadow
          className={stickyClassnames}
          childRef={buyboxActionsRef}
          forwardRef={buyboxActionsRef}
          offset={18}
          position="bottom"
          stickyByDefault
          stickyType="forwardRef"
          visibleByDefault
        >
          {!showFavoriteAndQuote ? (
            <div className="buybox__actions--flex">
              {quantityNode}
              {addToCartNode}
            </div>
          ) : (
            <div>
              <div className="buybox__actions--flex">
                {quantityNode}
                {addToCartNode}
              </div>
              <div className="buybox__actions--atl-atq">
                <BuyboxSaveToFavorites
                  quantity={quantity}
                  itemId={itemId}
                  isStickyChild
                />
                <BuyboxAddToQuote
                  itemId={itemId}
                  fulfillmentLocation={
                    getFulfillmentLocation(fulfillment, fulfillmentMethod, storeZip, storeId, zipCode)
                  }
                  fulfillmentMethod={fulfillmentMethod}
                  quantity={quantity.toString()}
                  disabled={disableATC || quantityLimitExceed}
                />
              </div>
            </div>
          )}
        </Sticky>
      )}
      {!showFavoriteAndQuote ? (
        <div className={buyboxActionClasses}>
          {showDetaTimer && (
            <DetaCountDownTimer
              dynamicEtaMinutes={sthFulfillment?.dynamicEta?.minutes}
              dynamicEtaHours={sthFulfillment?.dynamicEta?.hours}
              showCountdownTime={showCountdownTime}
              inventoryQuantity={getMaxDeliverableQty({ sthLocation, bodfsLocation })}
              removeTimer={removeTimer}
              deliveryStartDate={sthFulfillment?.deliveryDates?.startDate}
              isLongDayName={false}
              deliveryTimeline={sthFulfillment?.deliveryTimeline}
              startDate={formatDate(sthFulfillment?.deliveryDates?.startDate, true)}
              zipCode={zipCode}
            />
          )}
          <div className="buybox__actions--flex">
            {quantityNode}
            <div className="buybox__actions--atc">
              <div ref={buyboxActionsRef}>
                {addToCartNode}
              </div>
              {(showInstantCheckout() || (!isTwoTileEnabledFS && !hideInstantCheckout)) && (
                <div data-testid="instant-checkout-mock" style={showCards ? {} : { minHeight: '80px' }}>
                  <InstantCheckout
                    fulfillmentMethod={fulfillmentMethod}
                    itemId={itemId}
                    quantity={quantity}
                    productAddOns={productAddOns}
                    hideInvitation={showCards}
                    disabled={(!isTwoTileEnabledFS && !showInstantCheckout())}
                  />
                </div>
              )}
              {shouldDisplaySubbuybox({
                fulfillment,
                fulfillmentMethod
              }) && paypalEligible && !hidePaypalCheckout && !isBogoSelected && (
                <Paypal
                  cartOptions={getCartOptions({
                    channel,
                    configurableItems,
                    fulfillmentMethod
                  })}
                  cartReqParams={getCartReqParams({
                    itemId,
                    configurableItems,
                    fulfillment,
                    fulfillmentMethod,
                    productAddOns,
                    quantity,
                    storeId,
                    storeZip,
                    zipCode
                  })}
                  channel={channel}
                  disabled={disableATC || quantityLimitExceed}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={buyboxActionClasses}>
          {showDetaTimer && (
            <DetaCountDownTimer
              dynamicEtaMinutes={sthFulfillment?.dynamicEta?.minutes || 10}
              dynamicEtaHours={sthFulfillment?.dynamicEta?.hours}
              showCountdownTime={showCountdownTime}
              inventoryQuantity={getMaxDeliverableQty({ sthLocation, bodfsLocation })}
              removeTimer={removeTimer}
              deliveryStartDate={sthFulfillment?.deliveryDates?.startDate}
              isLongDayName={false}
              deliveryTimeline={sthFulfillment?.deliveryTimeline}
              startDate={formatDate(sthFulfillment?.deliveryDates?.startDate, true)}
              zipCode={zipCode}
            />
          )}
          <div className="buybox__actions--flex">
            {quantityNode}
            {addToCartNode}
          </div>
          <div className="buybox__actions">
            <div className="buybox__actions--atl-atq" ref={buyboxActionsRef}>
              <BuyboxSaveToFavorites
                quantity={quantity}
                itemId={itemId}
                isStickyChild={false}
              />
              <BuyboxAddToQuote
                itemId={itemId}
                fulfillmentLocation={getFulfillmentLocation(fulfillment, fulfillmentMethod, storeZip, storeId, zipCode)}
                fulfillmentMethod={fulfillmentMethod}
                quantity={quantity.toString()}
                disabled={disableATC || quantityLimitExceed}
              />
            </div>
            {(!disableATC && !quantityLimitExceed)
            && !hideInstantCheckout && (
              <div style={showCards ? {} : { minHeight: '80px' }}>
                <InstantCheckout
                  fulfillmentMethod={fulfillmentMethod}
                  itemId={itemId}
                  quantity={quantity}
                  productAddOns={productAddOns}
                  hideInvitation={showCards}
                />
              </div>
            )}
            {shouldDisplaySubbuybox({
              fulfillment,
              fulfillmentMethod
            }) && paypalEligible && !hidePaypalCheckout && !isBogoSelected && (
              <Paypal
                cartOptions={getCartOptions({
                  channel,
                  configurableItems,
                  fulfillmentMethod
                })}
                cartReqParams={getCartReqParams({
                  itemId,
                  configurableItems,
                  fulfillment,
                  fulfillmentMethod,
                  productAddOns,
                  quantity,
                  storeId,
                  storeZip,
                  zipCode
                })}
                channel={channel}
                disabled={disableATC || quantityLimitExceed}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

BuyboxActions.propTypes = {
  alterBrowserHistory: bool,
  disableATC: bool,
  fulfillment: shape({
    type: string,
    locations: arrayOf(shape({
      isAnchor: bool,
      inventory: shape({
        quantity: number,
        isInStock: bool,
        isLimitedQuantity: bool,
        isBackordered: bool
      })
    }))
  }),
  fulfillmentMethod: string,
  hideInstantCheckout: bool,
  hideQuantity: bool,
  isAssembleItemAtStore: bool,
  itemId: string.isRequired,
  configurableItems: shape({
    itemId: string,
    isAnchor: bool
  }),
  minimumOrderQuantity: number,
  onATCClick: func,
  onQuantityChange: func,
  productAddOns: shape({
    attachedLabor: arrayOf(shape({
      attachedLaborSku: string
    })),
    warrantyItem: shape({
      itemId: string
    })
  }),
  paypalEligible: bool,
  quantity: number,
  quantityLimitExceed: bool,
  hidePaypalCheckout: bool,
  sticky: bool,
  storeId: string,
  storeZip: string,
  zipCode: string,
  showCards: bool,
  showFavoriteAndQuote: bool,
  isTwoTileEnabledFS: bool,
  maxQuantityExceeded: bool,
  quantityLimit: number
};

BuyboxActions.defaultProps = {
  alterBrowserHistory: false,
  configurableItems: null,
  disableATC: false,
  fulfillment: null,
  fulfillmentMethod: '',
  hideInstantCheckout: false,
  hideQuantity: false,
  isAssembleItemAtStore: null,
  minimumOrderQuantity: 1,
  onATCClick: () => {},
  onQuantityChange: () => {},
  paypalEligible: false,
  productAddOns: null,
  quantity: 1,
  quantityLimitExceed: false,
  hidePaypalCheckout: false,
  sticky: false,
  storeId: null,
  storeZip: null,
  zipCode: null,
  showCards: null,
  showFavoriteAndQuote: false,
  isTwoTileEnabledFS: false,
  maxQuantityExceeded: false,
  quantityLimit: 0
};

export { BuyboxActions };
